import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Redirect } from 'react-router-dom';
import dayjs from 'dayjs';
import AuditLogEntriesAPI from '../../api/v3/audit-log-entries-api-v3';
import canAccessAuditLogQc from '../../utils/auditLogQcAccess';

const BASE = '/app/sales/bagspa';

const DEFAULT_FILTERS = {
  q: '',
  order_no: '',
  activity: '',
  checker: '',
  date_from: '',
  date_to: '',
};

const ACTIVITY_OPTIONS = [
  { value: '', label: 'All activities' },
  { value: 'qc1_checked', label: 'QC1 checked' },
  { value: 'qc2_checked', label: 'QC2 checked' },
  { value: 'qc_declined', label: 'QC declined' },
];

function fmtDateTime(value) {
  if (!value) return '—';
  return dayjs(value).format('DD MMM YYYY HH:mm');
}

function fmtStatus(value) {
  if (!value) return '—';
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function buildSummary(row) {
  return row?.summary || '—';
}

function renderChecker(display, email) {
  return (
    <>
      <div>{display || '—'}</div>
      {email && email !== display ? (
        <div className="small text-muted">{email}</div>
      ) : null}
    </>
  );
}

export default function AuditLogPage() {
  const hasAccess = canAccessAuditLogQc();

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, per: 25, total: 0 });
  const [page, setPage] = useState(1);
  const [per, setPer] = useState(25);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    if (!hasAccess) {
      setLoading(false);
      setErr('');
      setRows([]);
      setMeta({ page: 1, per: 25, total: 0 });
      return;
    }

    try {
      setLoading(true);
      setErr('');

      const json = await AuditLogEntriesAPI.list({
        ...appliedFilters,
        page,
        per,
      });

      setRows(Array.isArray(json?.items) ? json.items : []);
      setMeta(json?.meta || { page, per, total: 0 });
    } catch (error) {
      setErr(error.message || 'Failed to load audit logs.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [hasAccess, appliedFilters, page, per]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = useMemo(() => {
    const total = Number(meta?.total || 0);
    return Math.max(1, Math.ceil(total / per));
  }, [meta, per]);

  function onApply(event) {
    event.preventDefault();
    setPage(1);
    setAppliedFilters({ ...filters });
  }

  function onReset() {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setPage(1);
  }

  if (!hasAccess) {
    return <Redirect to={`${BASE}/dashboard`} />;
  }

  return (
    <div className="container-fluid px-0">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="mb-1">Audit Log QC</h2>
          <div className="text-muted">
            Read-only log untuk aktivitas QC1 checked, QC2 checked, dan QC declined.
          </div>
        </div>
      </div>

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <form onSubmit={onApply}>
            <div className="form-row">
              <div className="form-group col-md-3">
                <label>Search</label>
                <input
                  className="form-control"
                  value={filters.q}
                  onChange={(event) => setFilters((prev) => ({ ...prev, q: event.target.value }))}
                  placeholder="Search all fields"
                />
              </div>

              <div className="form-group col-md-2">
                <label>Order Number</label>
                <input
                  className="form-control"
                  value={filters.order_no}
                  onChange={(event) => setFilters((prev) => ({ ...prev, order_no: event.target.value }))}
                  placeholder="ORD-..."
                />
              </div>

              <div className="form-group col-md-2">
                <label>Activity</label>
                <select
                  className="form-control"
                  value={filters.activity}
                  onChange={(event) => setFilters((prev) => ({ ...prev, activity: event.target.value }))}
                >
                  {ACTIVITY_OPTIONS.map((option) => (
                    <option key={option.value || 'all'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group col-md-2">
                <label>Checker</label>
                <input
                  className="form-control"
                  value={filters.checker}
                  onChange={(event) => setFilters((prev) => ({ ...prev, checker: event.target.value }))}
                  placeholder="Email / name"
                />
              </div>

              <div className="form-group col-md-1">
                <label>From</label>
                <input
                  type="date"
                  className="form-control"
                  value={filters.date_from}
                  onChange={(event) => setFilters((prev) => ({ ...prev, date_from: event.target.value }))}
                />
              </div>

              <div className="form-group col-md-1">
                <label>To</label>
                <input
                  type="date"
                  className="form-control"
                  value={filters.date_to}
                  onChange={(event) => setFilters((prev) => ({ ...prev, date_to: event.target.value }))}
                />
              </div>

              <div className="form-group col-md-1 d-flex align-items-end">
                <button type="submit" className="btn btn-primary btn-block">
                  Apply
                </button>
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onReset}>
                Reset Filters
              </button>

              <div className="form-inline">
                <label className="mr-2 mb-0">Rows</label>
                <select
                  className="form-control form-control-sm"
                  value={per}
                  onChange={(event) => {
                    setPer(Number(event.target.value));
                    setPage(1);
                  }}
                >
                  {[10, 25, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </form>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="p-4 text-center text-muted">Loading audit logs...</div>
          ) : err ? (
            <div className="p-4 text-danger">{err}</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover mb-0">
                <thead className="thead-light">
                  <tr>
                    <th style={{ minWidth: 170 }}>Date Time</th>
                    <th style={{ minWidth: 150 }}>Order No</th>
                    <th style={{ minWidth: 140 }}>Activity QC1</th>
                    <th style={{ minWidth: 140 }}>Activity QC2</th>
                    <th style={{ minWidth: 220 }}>QC1 Done By</th>
                    <th style={{ minWidth: 220 }}>QC2 Done By</th>
                    <th style={{ minWidth: 220 }}>Summary</th>
                    <th style={{ minWidth: 260 }}>Before / After</th>
                  </tr>
                </thead>
                <tbody>
                  {!rows.length ? (
                    <tr>
                      <td colSpan={8} className="text-center text-muted py-4">
                        No QC audit logs found.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={row.id}>
                        <td>{fmtDateTime(row.done_at)}</td>
                        <td>
                          {row.order_id ? (
                            <Link to={`${BASE}/orders/${row.order_id}`}>
                              {row.order_no || `#${row.order_id}`}
                            </Link>
                          ) : (
                            row.order_no || '—'
                          )}
                        </td>
                        <td>{row.qc1_activity_label || '—'}</td>
                        <td>{row.qc2_activity_label || '—'}</td>
                        <td>{renderChecker(row.qc1_checker_display, row.qc1_checker_email)}</td>
                        <td>{renderChecker(row.qc2_checker_display, row.qc2_checker_email)}</td>
                        <td>{buildSummary(row)}</td>
                        <td>
                          <details>
                            <summary className="text-primary" style={{ cursor: 'pointer' }}>
                              View details
                            </summary>
                            <pre
                              className="bg-light border rounded p-2 mt-2 mb-0"
                              style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}
                            >
                              {JSON.stringify({ before: row.before, after: row.after }, null, 2)}
                            </pre>
                          </details>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card-footer d-flex justify-content-between align-items-center">
          <div className="text-muted small">
            Total {Number(meta?.total || 0).toLocaleString('id-ID')} item
          </div>

          <div className="btn-group">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={page <= 1 || loading}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            >
              Prev
            </button>
            <button type="button" className="btn btn-light btn-sm" disabled>
              Page {page} / {totalPages}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}